import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SessionDto,
  TotpConfirmInput,
  TwoFactorPolicyDto,
  TwoFactorPolicyInput,
  TwoFactorReauthInput,
  TwoFactorRecoveryCodesDto,
  TwoFactorSetupDto,
  TwoFactorStatusDto,
} from '@cpm/shared';
import { api } from './client.js';

// ── Two-factor (self-service) ────────────────────────────────────────

export function useTwoFactorStatus() {
  return useQuery({
    queryKey: ['2fa', 'status'],
    queryFn: () => api.get<TwoFactorStatusDto>('/api/auth/2fa'),
  });
}

/** Invalidate everything the 2FA state feeds: status, and the me/2FA flags. */
function useInvalidate2fa() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['2fa', 'status'] });
    void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  };
}

export function useStartTwoFactor() {
  return useMutation({
    mutationFn: () => api.post<TwoFactorSetupDto>('/api/auth/2fa/setup'),
  });
}

/**
 * Confirm enrolment. Deliberately does NOT invalidate here — the caller shows
 * the returned recovery codes once and invalidates only when the user
 * acknowledges them (so the forced-enrolment gate cannot tear the codes off
 * screen the instant 2FA flips on). Use `useInvalidateTwoFactor` on ack.
 */
export function useConfirmTwoFactor() {
  return useMutation({
    mutationFn: (input: TotpConfirmInput) =>
      api.post<TwoFactorRecoveryCodesDto>('/api/auth/2fa/confirm', input),
  });
}

export const useInvalidateTwoFactor = useInvalidate2fa;

export function useDisableTwoFactor() {
  const invalidate = useInvalidate2fa();
  return useMutation({
    mutationFn: (input: TwoFactorReauthInput) => api.post('/api/auth/2fa/disable', input),
    onSuccess: invalidate,
  });
}

export function useRegenerateRecoveryCodes() {
  const invalidate = useInvalidate2fa();
  return useMutation({
    mutationFn: (input: TwoFactorReauthInput) =>
      api.post<TwoFactorRecoveryCodesDto>('/api/auth/2fa/recovery-codes', input),
    onSuccess: invalidate,
  });
}

// ── Admin 2FA policy ─────────────────────────────────────────────────

export function useTwoFactorPolicy() {
  return useQuery({
    queryKey: ['2fa', 'policy'],
    queryFn: () => api.get<TwoFactorPolicyDto>('/api/auth/2fa/policy'),
  });
}

export function useSetTwoFactorPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TwoFactorPolicyInput) =>
      api.put<TwoFactorPolicyDto>('/api/auth/2fa/policy', input),
    onSuccess: (policy) => {
      queryClient.setQueryData(['2fa', 'policy'], policy);
    },
  });
}

// ── Sessions ─────────────────────────────────────────────────────────

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<SessionDto[]>('/api/auth/sessions'),
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/auth/sessions/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
      // Revoking the current session logs you out — refresh identity too.
      void queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useRevokeOtherSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/sessions/revoke-others'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });
}
